// src/components/documents/DocumentForm.tsx
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { useDocumentStore } from "@/store/documentStore"
import { toast } from "@/hooks/use-toast"
import { Upload, X } from "lucide-react"
import { useState } from "react"

// === ДИЗАЙН-СЛОЙ ===
import { documentFormTheme } from "./ui/DocumentFormUI"

const formSchema = z.object({
  title: z.string().min(3, "Название слишком короткое"),
  description: z.string().min(10, "Опишите документ подробнее"),
  type: z.enum(["report", "act", "order", "memo", "contract", "other"]),
})

type FormData = z.infer<typeof formSchema>

interface DocumentFormProps {
  onClose: () => void
}

export default function DocumentForm({ onClose }: DocumentFormProps) {
  const { createDocument } = useDocumentStore()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      type: "report",
    },
  })

  const onSubmit = async (data: FormData) => {
    setUploading(true)

    try {
      const docId = await createDocument(
        {
          title: data.title,
          description: data.description,
          type: data.type,
        },
        file || undefined
      )

      if (docId) {
        toast({
          title: "Успех",
          description: "Документ создан и отправлен на согласование",
        })
        onClose()
      }
    } catch (err: any) {
      toast({
        title: "Ошибка",
        description: err.message || "Не удалось создать документ",
        variant: "destructive",
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={documentFormTheme.mainContainer}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Label className={documentFormTheme.label}>Название</Label>
          <Input
            {...form.register("title")}
            className={documentFormTheme.input}
            placeholder="Например: Отчёт по работе за январь"
          />
          {form.formState.errors.title && (
            <p className={documentFormTheme.errorText}>
              {form.formState.errors.title.message}
            </p>
          )}
        </div>

        <div>
          <Label className={documentFormTheme.label}>Описание</Label>
          <Textarea
            {...form.register("description")}
            className={documentFormTheme.textarea}
            placeholder="Подробно опишите суть документа..."
          />
          {form.formState.errors.description && (
            <p className={documentFormTheme.errorText}>
              {form.formState.errors.description.message}
            </p>
          )}
        </div>

        <div>
          <Label className={documentFormTheme.label}>Тип документа</Label>
          <Select onValueChange={(v) => form.setValue("type", v as any)}>
            <SelectTrigger className={documentFormTheme.selectTrigger}>
              <SelectValue placeholder="Выберите тип" />
            </SelectTrigger>
            <SelectContent className={documentFormTheme.selectContent}>
              <SelectItem value="report" className={documentFormTheme.selectItem}>Отчёт</SelectItem>
              <SelectItem value="act" className={documentFormTheme.selectItem}>Акт</SelectItem>
              <SelectItem value="order" className={documentFormTheme.selectItem}>Приказ</SelectItem>
              <SelectItem value="memo" className={documentFormTheme.selectItem}>Служебная записка</SelectItem>
              <SelectItem value="contract" className={documentFormTheme.selectItem}>Договор</SelectItem>
              <SelectItem value="other" className={documentFormTheme.selectItem}>Другое</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className={documentFormTheme.label}>Прикрепить файл</Label>
          <div className="mt-2 flex items-center gap-4">
            <label className={documentFormTheme.fileUploadArea}>
              <Upload className="h-5 w-5" />
              <span>{file ? file.name : "Выбрать файл"}</span>
              <input
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
            </label>
            {file && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
                className="text-red-400 hover:text-red-300"
              >
                <X className="h-4 w-4 mr-1" /> Удалить
              </Button>
            )}
          </div>
        </div>

        <div className={documentFormTheme.footer}>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className={documentFormTheme.cancelButton}
          >
            Отмена
          </Button>
          <Button
            type="submit"
            disabled={uploading}
            className={documentFormTheme.submitButton}
          >
            {uploading ? "Создание..." : "Создать документ"}
          </Button>
        </div>
      </form>
    </div>
  )
}